import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, ShoppingBag, Sparkles, Loader2, Calendar, Clock, ChevronDown, ChevronUp, Truck, MapPin, Pencil } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const SLOT_LABELS = {
  outfit: "Outfit",
  jewellery: "Jewellery",
  clutch: "Clutches & Potlis",
  accessory: "Accessory",
};

function LookItemRow({ slotKey, item, onAddToCart, addingItemId }) {
  if (!item) return null;
  const isAdding = addingItemId === item.id;

  return (
    <div className="flex items-center gap-3 py-3 border-b border-[#282828]/5 last:border-b-0" data-testid={`look-item-${item.id}`}>
      <img
        src={item.images?.[0] || item.image || "/placeholder.jpg"}
        alt={item.name}
        className="w-14 h-14 object-cover rounded flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className="text-[12px] text-[#282828] font-medium truncate">{item.name}</p>
        <p className="text-[10px] text-[#676664]/60 uppercase tracking-wider">{SLOT_LABELS[slotKey]}</p>
        {item.rental_option_name && (
          <p className="text-[10px] text-[#9A7B4F] mt-0.5">{item.rental_option_name}</p>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-[13px] text-[#9A7B4F] font-semibold">${(item.rental_price || 0).toFixed(0)}</p>
      </div>
      <button
        onClick={() => onAddToCart(item, slotKey)}
        disabled={isAdding}
        className="flex-shrink-0 px-3 py-1.5 border border-[#9A7B4F]/30 text-[#9A7B4F] text-[9px] tracking-wider uppercase rounded hover:bg-[#9A7B4F]/5 transition-colors disabled:opacity-50"
        data-testid={`add-item-to-cart-${item.id}`}
      >
        {isAdding ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add"}
      </button>
    </div>
  );
}

function StyledLookCard({ look, onDelete, onAddAllToCart, onAddItemToCart, onRebuild, addingAll, addingItemId }) {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const slots = look.slots || {};
  const filledSlots = Object.entries(slots).filter(([_, item]) => item);
  const itemCount = filledSlots.length;

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this saved look?")) return;
    setDeleting(true);
    await onDelete(look.id);
    setDeleting(false);
  };

  const images = [
    slots.outfit?.images?.[0] || slots.outfit?.image,
    slots.jewellery?.images?.[0] || slots.jewellery?.image,
    slots.clutch?.images?.[0] || slots.clutch?.image,
    slots.accessory?.images?.[0] || slots.accessory?.image,
  ].filter(Boolean);

  return (
    <div className="bg-white border border-[#282828]/8 rounded-lg overflow-hidden" data-testid={`styled-look-${look.id}`}>
      {/* Image Grid */}
      <div className="grid grid-cols-4 gap-0.5 bg-[#282828]/5">
        {images.map((img, idx) => (
          <div key={idx} className="aspect-square">
            <img src={img} alt="" className="w-full h-full object-cover" />
          </div>
        ))}
        {Array(4 - images.length).fill(null).map((_, idx) => (
          <div key={`empty-${idx}`} className="aspect-square bg-[#FAF8F0]" />
        ))}
      </div>

      <div className="p-4">
        {/* Summary row */}
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
          <span className="text-[12px] text-[#9A7B4F] font-medium">{itemCount} Items{look.discount_percent > 0 ? ` \u2022 ${look.discount_percent}% Off` : ""}</span>
        </div>

        {/* Event + Rental + Delivery info */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
          {look.event_date && (
            <div className="flex items-center gap-1.5 text-[11px] text-[#676664]/70">
              <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>{new Date(look.event_date).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>
            </div>
          )}
          {look.rental_period && (
            <div className="flex items-center gap-1.5 text-[11px] text-[#676664]/70">
              <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>{look.rental_period}-day rental</span>
            </div>
          )}
          {look.delivery_method && (
            <div className="flex items-center gap-1.5 text-[11px] text-[#676664]/70">
              {look.delivery_method === "delivery" ? <Truck className="w-3.5 h-3.5" strokeWidth={1.5} /> : <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />}
              <span className="capitalize">{look.delivery_method === "pickup" ? "Pick Up" : "Delivery"}</span>
            </div>
          )}
        </div>

        {/* Pricing */}
        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-[18px] text-[#9A7B4F] font-semibold" data-testid={`look-price-${look.id}`}>${(look.final_price || 0).toFixed(0)}</span>
          {look.original_total > look.final_price && (
            <span className="text-[12px] text-[#676664]/40 line-through">${(look.original_total || 0).toFixed(0)}</span>
          )}
          {look.total_bond > 0 && (
            <span className="text-[10px] text-[#676664]/50">+ ${look.total_bond.toFixed(0)} bond</span>
          )}
        </div>

        {/* Expand / Collapse items */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between py-2 text-[11px] text-[#676664]/60 hover:text-[#676664] tracking-wider uppercase border-t border-[#282828]/5 transition-colors"
          data-testid={`toggle-items-${look.id}`}
        >
          <span>{expanded ? "Hide Items" : "View Items & Add Individually"}</span>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {/* Expanded item list */}
        {expanded && (
          <div className="mt-1" data-testid={`items-list-${look.id}`}>
            {filledSlots.map(([slotKey, item]) => (
              <LookItemRow
                key={slotKey}
                slotKey={slotKey}
                item={item}
                onAddToCart={onAddItemToCart}
                addingItemId={addingItemId}
              />
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="space-y-2 mt-3">
          <div className="flex gap-2">
            <Button
              onClick={() => onAddAllToCart(look)}
              disabled={addingAll === look.id}
              className="flex-1 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-2.5 text-[10px] tracking-[0.12em] uppercase"
              data-testid={`add-all-to-cart-${look.id}`}
            >
              {addingAll === look.id ? <Loader2 className="w-3 h-3 animate-spin" /> : (
                <>
                  <ShoppingBag className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                  Add All to Cart
                </>
              )}
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              variant="outline"
              className="px-3 border-[#282828]/15 text-[#676664]/60 hover:text-red-500 hover:border-red-200"
              data-testid={`delete-look-${look.id}`}
            >
              {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />}
            </Button>
          </div>
          <Button
            onClick={() => onRebuild(look.id)}
            variant="outline"
            className="w-full border-[#9A7B4F]/20 text-[#9A7B4F] py-2 text-[10px] tracking-[0.12em] uppercase hover:bg-[#9A7B4F]/5"
            data-testid={`rebuild-look-${look.id}`}
          >
            <Pencil className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
            Re-build This Look
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function MyStyledLooksPage() {
  const navigate = useNavigate();
  const { customer, loading: authLoading } = useCustomerAuth();
  const { addToCart } = useCart();
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingAll, setAddingAll] = useState(null);
  const [addingItemId, setAddingItemId] = useState(null);

  useEffect(() => {
    if (!authLoading && !customer) {
      navigate("/login?redirect=/account/my-styled-looks", { replace: true });
    }
  }, [customer, authLoading, navigate]);

  useEffect(() => {
    if (!customer) return;
    const fetchLooks = async () => {
      try {
        const customerId = customer.customer_id || customer.id;
        const res = await axios.get(`${API}/api/my-styled-looks`, {
          headers: { "x-customer-id": customerId }
        });
        setLooks(res.data.looks || []);
      } catch (err) {
        console.error("Failed to fetch styled looks:", err);
        toast.error("Failed to load your styled looks");
      } finally {
        setLoading(false);
      }
    };
    fetchLooks();
  }, [customer]);

  const handleDelete = async (lookId) => {
    try {
      const customerId = customer?.customer_id || customer?.id;
      await axios.delete(`${API}/api/my-styled-looks/${lookId}`, {
        headers: { "x-customer-id": customerId }
      });
      setLooks(prev => prev.filter(l => l.id !== lookId));
      toast.success("Look deleted");
    } catch (err) {
      console.error("Failed to delete look:", err);
      toast.error("Failed to delete look");
    }
  };

  const addItemToCart = async (item, look) => {
    return addToCart({
      outfit_id: item.id,
      event_date: look.event_date,
      duration: look.rental_period || 4,
      delivery_method: look.delivery_method || "pickup",
      rental_option_id: item.rental_option_id || null,
      rental_option_name: item.rental_option_name || null,
    });
  };

  const handleAddAllToCart = async (look) => {
    setAddingAll(look.id);
    try {
      const slots = look.slots || {};
      let addedCount = 0;
      for (const slotKey of ["outfit", "jewellery", "clutch", "accessory"]) {
        const item = slots[slotKey];
        if (item) {
          await addItemToCart(item, look);
          addedCount++;
        }
      }
      toast.success(`${addedCount} item${addedCount > 1 ? "s" : ""} added to cart!`);
      navigate("/cart");
    } catch (err) {
      console.error("Failed to add look to cart:", err);
      toast.error("Failed to add look to cart");
    } finally {
      setAddingAll(null);
    }
  };

  const handleAddSingleItem = async (item, slotKey) => {
    const look = looks.find(l => {
      const slots = l.slots || {};
      return Object.values(slots).some(s => s?.id === item.id);
    });
    if (!look) {
      toast.error("Could not find look details");
      return;
    }
    setAddingItemId(item.id);
    try {
      await addItemToCart(item, look);
      toast.success(`${item.name} added to cart!`);
    } catch (err) {
      console.error("Failed to add item to cart:", err);
      toast.error("Failed to add item to cart");
    } finally {
      setAddingItemId(null);
    }
  };

  const handleRebuild = (lookId) => {
    navigate(`/build-your-look?rebuildSavedLook=${lookId}`);
  };

  if (authLoading || !customer) return null;

  return (
    <div className="min-h-screen bg-[#FAF8F0] flex flex-col">
      <Navbar />

      <div className="flex-1 max-w-[1200px] w-full mx-auto px-4 lg:px-8 py-8 lg:py-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/account" className="text-[#676664]/50 hover:text-[#676664] transition-colors">
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </Link>
          <div>
            <p className="text-[10px] text-[#9A7B4F] tracking-[0.2em] uppercase mb-1">My Account</p>
            <h1 className="text-2xl lg:text-3xl text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              My Styled Looks
            </h1>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#9A7B4F]" />
          </div>
        ) : looks.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-looks-state">
            <Sparkles className="w-12 h-12 text-[#9A7B4F]/30 mx-auto mb-4" strokeWidth={1} />
            <h2 className="text-[18px] text-[#282828] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              No saved looks yet
            </h2>
            <p className="text-[13px] text-[#676664]/60 mb-6 max-w-md mx-auto">
              Create your perfect outfit combination with our Build Your Look feature and save it for later.
            </p>
            <Button
              onClick={() => navigate("/build-your-look")}
              className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-8 py-3 text-[11px] tracking-[0.15em] uppercase"
              data-testid="build-look-cta"
            >
              Build Your Look
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="looks-grid">
            {looks.map(look => (
              <StyledLookCard
                key={look.id}
                look={look}
                onDelete={handleDelete}
                onAddAllToCart={handleAddAllToCart}
                onAddItemToCart={handleAddSingleItem}
                onRebuild={handleRebuild}
                addingAll={addingAll}
                addingItemId={addingItemId}
              />
            ))}
          </div>
        )}

        {looks.length > 0 && (
          <div className="text-center mt-12 pt-8 border-t border-[#282828]/8">
            <p className="text-[13px] text-[#676664]/60 mb-4">Want to create another styled look?</p>
            <Button
              onClick={() => navigate("/build-your-look")}
              variant="outline"
              className="border-[#9A7B4F]/30 text-[#9A7B4F] px-6 py-2.5 text-[10px] tracking-[0.12em] uppercase hover:bg-[#9A7B4F]/5"
              data-testid="build-another-look-btn"
            >
              Build Another Look
            </Button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
